import { InboxOutlined, ReloadOutlined } from '@ant-design/icons';
import {
    Button,
    Checkbox,
    DatePicker,
    Modal,
    Space,
    Table,
    Tag,
    Typography,
    message as antdMessage,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../contexts/ApiContext';
import type { MetalCategory } from '../../domain/metals/metalCategories';
import {
    canAddPurchaseToStock,
    defaultPurchaseListRange,
    filterPurchases,
    purchaseListSummary,
    purchasePaymentKey,
    purchasesToAddToStock,
    type PurchaseListItem,
    type PurchaseListLine,
    type PurchaseListRange,
} from '../../domain/metals/purchaseList';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const DATE_FORMAT = 'YYYY-MM-DD';

const formatLei = (value: number): string => value.toFixed(0);

type Props = Readonly<{
    /** Raised after a purchase is recorded on the Purchase tab, so the list reloads. */
    refreshKey: number;
    /** Called after a purchase is put into stock, so the stock tabs show it. */
    onAddedToStock: () => void;
}>;

/** The lines of one purchase, under its row. */
function PurchaseLinesDetail({ lines }: Readonly<{ lines: PurchaseListLine[] }>) {
    const { t } = useTranslation();
    return (
        <Table<PurchaseListLine>
            data-testid="purchases-lines"
            size="small"
            pagination={false}
            rowKey="lineNo"
            dataSource={lines}
            columns={[
                { title: '#', dataIndex: 'lineNo' },
                {
                    title: t('metals.purchase.metal'),
                    dataIndex: 'metalType',
                    render: (metal: MetalCategory) => t(`metals.purchase.metals.${metal}`),
                },
                { title: t('metals.purchase.purity'), dataIndex: 'purity' },
                {
                    title: t('metals.purchase.quantity'),
                    dataIndex: 'quantity',
                    render: (value: number) => value.toFixed(2),
                },
                {
                    title: t('metals.purchase.unitPrice'),
                    dataIndex: 'unitPrice',
                    render: (value: number) => value.toFixed(2),
                },
                { title: t('metals.purchase.total'), dataIndex: 'total', render: formatLei },
            ]}
        />
    );
}

/** Metals page tab *Purchases*: the purchases of a period; one not in stock can be put into it. */
function PurchasesListContent({ refreshKey, onAddedToStock }: Props) {
    const { t } = useTranslation();
    const { metals } = useApi();
    const [messageApi, messageContextHolder] = antdMessage.useMessage();
    const [modal, modalContextHolder] = Modal.useModal();

    const [range, setRange] = useState<PurchaseListRange>(() =>
        defaultPurchaseListRange(dayjs().format(DATE_FORMAT)),
    );
    const [purchases, setPurchases] = useState<PurchaseListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [onlyNotInStock, setOnlyNotInStock] = useState(false);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [adding, setAdding] = useState(false);

    const loadPurchases = useCallback(async () => {
        setLoading(true);
        try {
            const result = await metals.listPurchases(range);
            if (!result.success) {
                messageApi.error(result.error ?? t('metals.purchases.loadFailed'));
                return;
            }
            setPurchases(result.data?.purchases ?? []);
        } finally {
            setLoading(false);
        }
    }, [metals, range, messageApi, t]);

    useEffect(() => {
        void loadPurchases();
    }, [loadPurchases, refreshKey]);

    const visible = useMemo(
        () => filterPurchases(purchases, onlyNotInStock),
        [purchases, onlyNotInStock],
    );
    const summary = useMemo(() => purchaseListSummary(visible), [visible]);
    const toAdd = useMemo(
        () => purchasesToAddToStock(purchases, selectedIds),
        [purchases, selectedIds],
    );

    const toggleSelected = (purchase: PurchaseListItem) => {
        if (!canAddPurchaseToStock(purchase)) {
            return;
        }
        setSelectedIds(ids =>
            ids.includes(purchase.id) ? ids.filter(id => id !== purchase.id) : [...ids, purchase.id],
        );
    };

    /** Puts the purchases into stock one after the other; the first that fails stops the rest. */
    const addSelectedToStock = async (chosen: PurchaseListItem[]) => {
        setAdding(true);
        const added: PurchaseListItem[] = [];
        try {
            for (const purchase of chosen) {
                const result = await metals.addPurchaseToStock(purchase.id);
                if (!result.success) {
                    messageApi.error(
                        t('metals.purchases.addFailedFor', {
                            id: purchase.id,
                            reason: result.error ?? t('metals.purchases.addFailed'),
                        }),
                    );
                    break;
                }
                added.push(purchase);
            }
            if (added.length > 0) {
                messageApi.success(
                    added.length === 1
                        ? t('metals.purchases.added', {
                              id: added[0].id,
                              client: added[0].clientName,
                          })
                        : t('metals.purchases.addedMany', { count: added.length }),
                );
                const addedIds = added.map(purchase => purchase.id);
                setSelectedIds(ids => ids.filter(id => !addedIds.includes(id)));
                onAddedToStock();
                await loadPurchases();
            }
        } finally {
            setAdding(false);
        }
    };

    const handleAddToStock = () => {
        if (toAdd.length === 0) {
            return;
        }
        const [first] = toAdd;
        const many = toAdd.length > 1;
        modal.confirm({
            title: t(
                many ? 'metals.purchases.confirmAddManyTitle' : 'metals.purchases.confirmAddTitle',
            ),
            content: many
                ? t('metals.purchases.confirmAddManyMessage', { count: toAdd.length })
                : t('metals.purchases.confirmAddMessage', {
                      id: first.id,
                      client: first.clientName,
                      date: first.purchaseDate,
                  }),
            okText: t('metals.purchases.addToStock'),
            centered: true,
            onOk: () => addSelectedToStock(toAdd),
        });
    };

    const columns: ColumnsType<PurchaseListItem> = [
        { title: t('metals.purchases.id'), dataIndex: 'id', width: 70 },
        { title: t('metals.purchases.date'), dataIndex: 'purchaseDate', width: 110 },
        { title: t('metals.purchases.client'), dataIndex: 'clientName' },
        {
            title: t('metals.purchases.total'),
            dataIndex: 'totalRon',
            align: 'right',
            render: formatLei,
        },
        {
            title: t('metals.purchases.payment'),
            key: 'payment',
            render: (_value, purchase) => t(purchasePaymentKey(purchase)),
        },
        {
            title: t('metals.purchases.cashPoint'),
            dataIndex: 'cashPointName',
            render: (name: string | null) => name ?? '-',
        },
        {
            title: t('metals.purchases.disposition'),
            dataIndex: 'dispositionId',
            render: (id: number | null) => id ?? '-',
        },
        {
            title: t('metals.purchases.stock'),
            dataIndex: 'addedToStock',
            render: (inStock: boolean) =>
                inStock ? (
                    <Tag color="green">{t('metals.purchases.inStock')}</Tag>
                ) : (
                    <Tag color="orange">{t('metals.purchases.notInStock')}</Tag>
                ),
        },
        {
            title: t('metals.purchases.user'),
            dataIndex: 'userName',
            render: (name: string | null) => name ?? '-',
        },
    ];

    return (
        <div data-testid="metals-purchases">
            {messageContextHolder}
            {modalContextHolder}
            <Space wrap style={{ marginBottom: 16 }} align="center">
                <Text type="secondary">{t('metals.purchases.range')}</Text>
                <RangePicker
                    data-testid="purchases-range"
                    allowClear={false}
                    value={[dayjs(range.from), dayjs(range.to)]}
                    onChange={values => {
                        if (values?.[0] && values[1]) {
                            setRange({
                                from: values[0].format(DATE_FORMAT),
                                to: values[1].format(DATE_FORMAT),
                            });
                        }
                    }}
                />
                <Checkbox
                    data-testid="purchases-only-not-in-stock"
                    checked={onlyNotInStock}
                    onChange={event => setOnlyNotInStock(event.target.checked)}
                >
                    {t('metals.purchases.onlyNotInStock')}
                </Checkbox>
                <Button icon={<ReloadOutlined />} onClick={() => void loadPurchases()}>
                    {t('metals.purchases.refresh')}
                </Button>
                <Button
                    type="primary"
                    data-testid="purchases-add-to-stock"
                    icon={<InboxOutlined />}
                    disabled={toAdd.length === 0}
                    loading={adding}
                    onClick={handleAddToStock}
                >
                    {t('metals.purchases.addToStock')}
                </Button>
            </Space>
            <div style={{ marginBottom: 8 }}>
                <Text type="secondary">{t('metals.purchases.selectHint')}</Text>
            </div>
            <Table<PurchaseListItem>
                data-testid="purchases-table"
                size="small"
                rowKey="id"
                loading={loading}
                dataSource={visible}
                columns={columns}
                pagination={{ pageSize: 20, hideOnSinglePage: true }}
                locale={{ emptyText: t('metals.purchases.empty') }}
                rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedIds,
                    // A purchase already in stock cannot go in again, so it cannot be ticked.
                    getCheckboxProps: purchase => ({
                        disabled: !canAddPurchaseToStock(purchase),
                    }),
                    onChange: keys => setSelectedIds(keys.map(Number)),
                }}
                onRow={purchase => ({ onClick: () => toggleSelected(purchase) })}
                expandable={{
                    expandedRowRender: purchase => <PurchaseLinesDetail lines={purchase.lines} />,
                }}
            />
            <div style={{ marginTop: 8, textAlign: 'right' }}>
                <Text strong data-testid="purchases-summary">
                    {t('metals.purchases.summary', {
                        count: summary.count,
                        total: formatLei(summary.totalRon),
                        notInStock: summary.notInStock,
                    })}
                </Text>
            </div>
        </div>
    );
}

export default PurchasesListContent;
